import React from 'react'
import EditIcon from 'assets/icons/edit-icon'
import RemoveIcon from 'assets/icons/remove-icon16px'
import EyeIcon from 'assets/icons/eye-2-line-icon'
import NominateIcon from 'assets/icons/nominate-icon'
import ViewIcon from 'assets/icons/view-icon'
import ReassessIcon from 'assets/icons/reassess-icon'
import { buildAboutUrl, buildDetailsUrl } from 'routing/constants'
import { requestReassesSite, requestEditSite } from 'api/site'
import { showErrorToast } from 'utils/toast'
import {
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DELISTED_ID,
  SITE_STATUS_DOES_NOT_QUALIFY,
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_REVIEWED_BY_RFP_ID,
} from '../siteStatuses'
import { getEditOnlyContext } from '../../utils/editOnlyContext'
import {
  createNavigateIndividualReviewedByRfp,
  createNavigateViewSiteWithAllHistories,
  handleCreateMonitoring,
  handleGetMonitoring,
} from './navigationHandlers'
import AddMonitoringIcon from 'assets/icons/add-monitoring-icon'
import { buildMonitoringEvaluationUrl } from 'routing/constants'

function buildNcgDraftOrEditOnlyItems(site, history, setSiteForDeleting) {
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  return [
    {
      icon: <EditIcon />,
      title: 'edit',
      onClick: createNavigateIndividualReviewedByRfp(site, history, editOnlySite, editOnlySiteStatus),
    },
    {
      icon: <RemoveIcon />,
      title: 'Remove',
      onClick: () => {
        setSiteForDeleting(site)
      },
    },
  ]
}

function buildNcgReviewedByRfpItems(site, history, setSiteToWorkWith) {
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  return [
    {
      icon: <EyeIcon />,
      title: 'review',
      onClick: createNavigateIndividualReviewedByRfp(site, history, editOnlySite, editOnlySiteStatus),
    },
    {
      icon: <NominateIcon />,
      title: 'nominateSite',
      onClick: () => {
        setSiteToWorkWith(site)
      },
    },
  ]
}

function buildNcgConfirmedDelistedDnqItems(site, history, profile, isMySite, setMonitoringForDeleting) {
  const { editOnlySite } = getEditOnlyContext(site)
  return [
    {
      icon: <ViewIcon />,
      title: 'viewSite',
      // Open parent Site so approved edit-only merges are visible (not a stale history snapshot).
      onClick: () => history(buildDetailsUrl(site.id)),
    },
    {
      icon: <ReassessIcon />,
      title: 'reassessSite',
      disabled: false,
      onClick: () => {
        requestReassesSite(site.id).then((data) => {
          if (data.error) {
            return showErrorToast(data.error, 'showBorder')
          }
          return history(buildDetailsUrl(site.id, data.id, editOnlySite))
        })
      },
    },
    {
      icon: <EditIcon />,
      title: 'editNow',
      disabled: false,
      onClick: () => {
        requestEditSite(site.id).then((data) => {
          if (data.error) {
            return showErrorToast(data.error, 'showBorder')
          }
          const parentSiteId = data.siteId || site.id
          const editOnlySiteId = data.id
          return history(buildAboutUrl(parentSiteId, editOnlySiteId, editOnlySite))
        })
      },
    },
    ...(() => {
      const siteEvaluations = Array.isArray(site?.monitoring)
        ? [...site.monitoring].sort((a, b) => Number(b.id) - Number(a.id))
        : []
      const confirmedMonitoring = siteEvaluations.find((entry) => entry?.monitoring_status === SITE_STATUS_CONFIRMED_ID)
      return confirmedMonitoring
        ? [
          {
            icon: <EyeIcon />,
            title: 'viewMonitoringEvaluation',
            disabled: false,
            onClick: () => {
              handleGetMonitoring(site.id, confirmedMonitoring.id, history)
            }
          }
        ]
        : []
    })(),
    (() => {
      const siteEvaluations = site?.monitoring
      const isUserCoProposer = site?.isUserCoProposer
      let targetEvaluation = null
      let lockedByOtherUser = false
      let alreadyEvaluated
      if (Array.isArray(siteEvaluations)) {
        targetEvaluation = siteEvaluations.find((entry) => {
          return entry && (entry.monitor_users_id === profile.id || isUserCoProposer) && entry.monitoring_status === SITE_STATUS_DRAFT_ID
        })
        lockedByOtherUser = siteEvaluations.some((entry) => {
          const isDraft = entry && entry.monitoring_status === SITE_STATUS_DRAFT_ID
          const isOwnedByOther = entry && entry.monitor_users_id !== profile.id
          return isDraft && isOwnedByOther
        })
        alreadyEvaluated = siteEvaluations.some((entry) => entry?.monitoring_status === SITE_STATUS_CONFIRMED_ID)
      }
      const title =
        targetEvaluation && alreadyEvaluated
          ? 'editMonitoringRevaluation'
          : targetEvaluation
            ? 'editMonitoringEvaluation'
            : alreadyEvaluated && !lockedByOtherUser
              ? 'reEvaluateMonitoring'
              : 'addMonitoringEvaluation'
      const isDisabled = lockedByOtherUser && !isUserCoProposer
      return {
        icon: targetEvaluation ? <EditIcon /> : <AddMonitoringIcon />,
        title,
        disabled: isDisabled,
        onClick: () => {
          if (isDisabled) return
          if (title === 'addMonitoringEvaluation' || title === 'reEvaluateMonitoring') {
            localStorage.setItem('isEdit', 'true')
          } else if (title === 'editMonitoringEvaluation' || title === 'editMonitoringRevaluation') {
            localStorage.setItem('isEdit', 'false')
          }
          if (targetEvaluation && (targetEvaluation.id || targetEvaluation.monitoringId)) {
            const monitoringId = targetEvaluation.id || targetEvaluation.monitoringId
            return history(buildMonitoringEvaluationUrl(site.id, monitoringId))
          }
          targetEvaluation
            ? handleGetMonitoring(site.id, targetEvaluation.id, history)
            : handleCreateMonitoring(site.id, profile.id, history)
        }
      }
    })(),
    ...(() => {
      const siteEvaluations = site?.monitoring
      let targetEvaluation = null
      if (Array.isArray(siteEvaluations)) {
        targetEvaluation = siteEvaluations.find((entry) => {
          return entry && entry.monitor_users_id === profile.id && entry.monitoring_status === SITE_STATUS_DRAFT_ID
        })
      }
      return isMySite && targetEvaluation
        ? [
          {
            icon: <RemoveIcon />,
            title: 'removeMonitoring',
            disabled: false,
            onClick: () => {
              setMonitoringForDeleting(targetEvaluation)
            }
          }
        ]
        : []
    })()
  ]
}

function buildNcgDefaultItems(site, history) {
  const { editOnlySite, editOnlySiteStatus } = getEditOnlyContext(site)
  return [
    {
      icon: <ViewIcon />,
      title: 'viewSite',
      onClick: createNavigateViewSiteWithAllHistories(site, history, editOnlySite, editOnlySiteStatus),
    },
  ]
}

export function buildNcgMenuItems(ctx) {
  const { site, history, setSiteToWorkWith, setSiteForDeleting, profile, isMySite, setMonitoringForDeleting } = ctx

  if ([SITE_STATUS_DRAFT_ID, SITE_STATUS_EDIT_ONLY_ID].includes(site.SiteStatus.id)) {
    return buildNcgDraftOrEditOnlyItems(site, history, setSiteForDeleting)
  }
  if (site.SiteStatus.id === SITE_STATUS_REVIEWED_BY_RFP_ID) {
    return buildNcgReviewedByRfpItems(site, history, setSiteToWorkWith)
  }
  if (
    site.SiteStatus.id === SITE_STATUS_CONFIRMED_ID ||
    site.SiteStatus.id === SITE_STATUS_DELISTED_ID ||
    site.SiteStatus.id === SITE_STATUS_DOES_NOT_QUALIFY
  ) {
    return buildNcgConfirmedDelistedDnqItems(site, history, profile, isMySite, setMonitoringForDeleting)
  }
  return buildNcgDefaultItems(site, history)
}

export function getNcgActionMenuItems(...args) {
  const [site, profile, history, setSiteToWorkWith, setSiteForDeleting, , , isMySite, setMonitoringForDeleting] = args
  return buildNcgMenuItems({
    site,
    history,
    setSiteToWorkWith,
    setSiteForDeleting,
    profile,
    isMySite,
    setMonitoringForDeleting,
  })
}
